import { useCallback, useState } from 'react';
import useToast from '../useToast';
import { listingConstants } from '../../constants/globalConstant';
import { useFeatureAnalytics } from '../useFeatureAnalytics';

/**
 * All feature-level CRUD actions (delete / clone / publish / unpublish /
 * updateTeams / create). Hosts publish-state (boolean). Delete mutates the
 * provided listing state optimistically and rolls back on failure.
 *
 * API layer is expected to reject on non-2xx (axios interceptor in
 * src/api/errorInterceptor.js shows the toast automatically). Actions here
 * only need `try/catch` for state cleanup, not for toasting errors —
 * unless the feature hasn't been migrated yet (still has
 * `.catch((err) => err.response)` in its API file).
 */
export function useFeatureActions({ config, isListing, listData, setListData, refetchTeams }) {
  const toast = useToast();
  const track = useFeatureAnalytics(config);
  const [isPublished, setIsPublished] = useState(false);
  const [isPublishLoading, setIsPublishLoading] = useState(false);

  const deleteItem = useCallback(
    async (id) => {
      // Read the row from the current list data (a prop). NOTE: never call
      // `setListData()` to read state — it is a React state setter, so calling
      // it with no argument sets the list to `undefined` and crashes consumers
      // (e.g. FeatureContext's `listing.data.length`).
      const item = Array.isArray(listData)
        ? listData.find((i) => i[config.idKey] === id) || null
        : null;

      const res = await config.apis.delete(id);
      setListData((prev) => (prev || []).filter((row) => row[config.idKey] !== id));

      // Track analytics if configured
      if (config?.analytics?.delete) {
        track('delete', { item, source: { location: 'listing' } });
      }

      toast.success(
        `${config.featureLabel} deleted successfully!`,
        `${config.featureLabel} Deleted`
      );
      refetchTeams({ silent: true });
      return res;
    },
    [config, toast, refetchTeams, listData, setListData, track]
  );

  const cloneItem = useCallback(
    async (payload) => {
      if (!config.apis.clone) return null;
      const res = await config.apis.clone(payload);

      // Track analytics if configured
      if (config?.analytics?.clone) {
        track('clone', { item: res?.data });
      }

      toast.success(`${config.featureLabel} cloned successfully!`, `${config.featureLabel} Cloned`);
      return res;
    },
    [config, toast, track]
  );

  const publishItem = useCallback(
    async ({ id, postBody }) => {
      setIsPublishLoading(true);
      try {
        const res = await config.apis.publish(id, postBody);
        setIsPublished(true);
        if (isListing) {
          setListData((prev) =>
            prev.map((item) =>
              item[config.idKey] === id ? { ...item, status: listingConstants.PUBLISHED } : item
            )
          );
        }
        refetchTeams();

        // Track analytics if configured
        if (config?.analytics?.publish) {
          track('publish', { id, postBody });
        }

        toast.success(
          `${config.featureLabel} published successfully!`,
          `${config.featureLabel} Published`
        );
        return res;
      } finally {
        setIsPublishLoading(false);
      }
    },
    [config, toast, refetchTeams, isListing, setListData, track]
  );

  const unpublishItem = useCallback(
    async (id, { trackLoading = true } = {}) => {
      if (trackLoading) setIsPublishLoading(true);
      try {
        const res = await config.apis.unpublish(id);
        setIsPublished(false);
        if (isListing) {
          setListData((prev) =>
            prev.map((item) =>
              item[config.idKey] === id ? { ...item, status: listingConstants.UNPUBLISHED } : item
            )
          );
        }
        refetchTeams({ silent: !trackLoading });

        // Track analytics if configured
        if (config?.analytics?.unpublish) {
          track('unpublish', { id });
        }

        toast.success(
          `${config.featureLabel} unpublished successfully!`,
          `${config.featureLabel} Unpublished`
        );
        return res;
      } finally {
        if (trackLoading) setIsPublishLoading(false);
      }
    },
    [config, toast, refetchTeams, isListing, setListData, track]
  );

  const updateTeams = useCallback(
    async ({ id, postBody }) => {
      setIsPublishLoading(true);
      try {
        const res = await config.apis.updateTeams(id, postBody);
        toast.success(
          `${config.featureLabel} team assignments updated successfully!`,
          `${config.featureLabel} Teams Updated`
        );
        refetchTeams();
        return res;
      } finally {
        setIsPublishLoading(false);
      }
    },
    [config, toast, refetchTeams]
  );

  const createItem = useCallback(
    async (payload) => {
      if (!config.apis.create) return null;
      const res = await config.apis.create(payload ?? config.createPayload);
      if (!res) return null;
      // Two contracts coexist:
      // - Pre-migration configs return the raw axios response (numeric HTTP
      //   `status`, incl. `err.response` on failure) → unwrap `.data`.
      // - Post-migration configs (e.g. homepage) unwrap the response and
      //   return the document itself — whose `status` may be a STRING like
      //   "draft", so it must not be run through the HTTP-status guard.
      let data;
      if (typeof res.status === 'number') {
        if (res.status !== 200 && res.status !== 201) {
          // Show error toast with backend message if available
          const errorMessage = res.data?.message || res.data?.error || 'Failed to create';
          toast.error(errorMessage, `${config.featureLabel} Creation Failed`);
          return null;
        }
        data = res.data;
      } else {
        data = res;
      }
      if (!data) return null;
      // Optional per-feature side effect (analytics, etc.).
      if (typeof config.onCreate === 'function') {
        try {
          config.onCreate(data);
        } catch {
          // Don't let analytics errors break the create flow.
        }
      }
      return data;
    },
    [config, toast]
  );

  return {
    deleteItem,
    cloneItem,
    publishItem,
    unpublishItem,
    updateTeams,
    createItem,
    isPublished,
    setIsPublished,
    isPublishLoading
  };
}
